import axios from 'axios';
import { useConnectionStore } from '../stores/connectionStore';
import { useGlobalStore } from '../stores/globalStore';
import { useTabletStore } from '../stores/tabletStore';
import { reportApiError, reportApiSuccess } from '../stores/apiHealthStore';
import { reportSessionExpired } from '../stores/sessionStore';
import {
  isSessionDead,
  markSessionDead,
  renewSession,
  resetSessionRenewal,
  setSessionAuthActions,
  type SessionAuthActions,
} from '../auth/sessionRenewal';
import { reportVersionMismatch } from '../stores/versionMismatchStore';
import { useDbUpdateStore, waitForDatabaseUpdate } from '../stores/dbUpdateStore';
import { recordFailedCall } from '../utils/feedbackContext';
import { isApiBlocked, isSessionExpiredError, SessionExpiredError } from '../auth/pollingGate';

// In development, don't set baseURL - let Vite's proxy handle /api/* routing.
// In production, use VITE_API_BASE_URL as the default. Version-specific
// connections dynamically override baseURL in the request interceptor.
export const VERSION_API_URLS: Record<string, string> = {
  '2.0': 'https://dietech-core-api-prod-v2-0.azurewebsites.net',
  '2.1': 'https://dietech-core-api-prod-v2-1.azurewebsites.net',
  '2.2': 'https://dietech-core-api-prod-v2-2.azurewebsites.net',
};

// Reverse map (host -> version) so the response interceptor can recognise a
// request that was routed to a pinned version slot from its baseURL alone.
const VERSION_HOST_TO_VERSION: Record<string, string> = Object.fromEntries(
  Object.entries(VERSION_API_URLS).map(([version, host]) => [host, version]),
);

/** Returns the pinned version (e.g. "2.0") a routed API host maps to, if any. */
export function versionForApiHost(host?: string | null): string | undefined {
  if (!host) return undefined;
  return VERSION_HOST_TO_VERSION[host];
}

/**
 * The API host a connection's requests actually resolve to, for display
 * (About dialog). Mirrors the routing in the request interceptor: dev always
 * goes through the Vite proxy; a pinned WebVersion goes to its slot; otherwise
 * the default prod API.
 */
export function getRoutedApiHost(webVersion?: string | null): string {
  if (import.meta.env.DEV) return 'Vite proxy → localhost:7142';
  if (webVersion && VERSION_API_URLS[webVersion]) return VERSION_API_URLS[webVersion];
  return import.meta.env.VITE_API_BASE_URL || 'Default';
}

const apiClient = axios.create({
  baseURL: import.meta.env.DEV ? '' : import.meta.env.VITE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// --- Auth token bridge (set by AuthTokenSetter component after login) ---
let getAccessToken: (() => string | undefined) | null = null;

export function setTokenGetter(getter: () => string | undefined) {
  getAccessToken = getter;
}

// --- Auth action bridge (set by AuthTokenSetter for 401 handling) ---
// Stored in auth/sessionRenewal.ts, which owns the one guarded renewal that
// this interceptor and the proactive renewal share.
export function setAuthActions(actions: SessionAuthActions | null) {
  setSessionAuthActions(actions);
}

/**
 * The headers every API request carries: the signed-in user's token,
 * their PKey_User and the selected connection. Exported for the few calls
 * apiClient cannot make (a streamed response, a request sent while the page
 * unloads); everything else goes through apiClient, which adds them itself.
 */
export function apiHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};

  const token = getAccessToken?.();
  if (token) headers.Authorization = `Bearer ${token}`;

  // Add current user's PKey_User so server-side DALs that hydrate
  // `DietechConnectionUserManager.CU_UserID` can populate
  // CU_UserName/CU_FName/CU_LName via FillUserInfo. Without this,
  // every audit-log-writing endpoint receives userId=0, FillUserInfo
  // short-circuits at its `CU_UserID <= 0` guard, and audit rows
  // (tblLog_Interaction / tblLog_Transaction) ship with blank
  // PMSec_User_* columns. Header name matches the [FromHeader]
  // binding on every controller that takes `int userId = 0`.
  const globalUserId = useGlobalStore.getState().userId;
  if (globalUserId && globalUserId > 0) headers['X-User-Id'] = String(globalUserId);

  const connection = useConnectionStore.getState().selectedConnection;
  if (connection?.connectionId) headers['X-Connection-Id'] = String(connection.connectionId);

  return headers;
}

/**
 * The API host a request for `url` goes to: a pinned WebVersion's slot, or
 * undefined for the client's default baseURL. Shared endpoints (connection
 * list and user lookup query DietechSoftwareMain, not facility DBs) always
 * use the main API.
 */
function routedBaseUrl(url: string): string | undefined {
  const connection = useConnectionStore.getState().selectedConnection;
  if (import.meta.env.DEV || !connection?.connectionId || !connection.webVersion) return undefined;
  const isSharedEndpoint = url.includes('/DietechConnection') || url.includes('/user/Me');
  return isSharedEndpoint ? undefined : VERSION_API_URLS[connection.webVersion];
}

/** The absolute URL apiClient would call for an API path, for fetch() callers. */
export function apiUrl(path: string): string {
  const base = routedBaseUrl(path) ?? apiClient.defaults.baseURL ?? '';
  return `${base}${path}`;
}

function headerOf(config: { headers?: unknown } | undefined, name: string): unknown {
  const headers = config?.headers as { get?: (name: string) => unknown; [name: string]: unknown } | undefined;
  return headers?.get?.(name) ?? headers?.[name];
}

/**
 * True when a request may not go out because the user's sign-in has ended
 * (the Session Expired dialog is up). Sending it could only come back 401.
 * Not for a registered tablet (its PIN session, not the user's sign-in), a
 * request carrying a device token (bedside, Selective Touch devices), or a
 * request that opts out with `_allowWhenExpired`.
 */
export function isBlockedBySessionGate(config: { headers?: unknown; _allowWhenExpired?: unknown }): boolean {
  if (!isApiBlocked()) return false;
  if (config._allowWhenExpired) return false;
  if (useTabletStore.getState().active) return false;
  if (headerOf(config, 'X-Device-Token')) return false;
  return true;
}

// Request interceptor: refuse once signed out, then add auth + connection headers + version routing
apiClient.interceptors.request.use((config) => {
  if (isBlockedBySessionGate(config)) {
    throw new SessionExpiredError(config);
  }

  for (const [name, value] of Object.entries(apiHeaders())) {
    config.headers[name] = value;
  }

  const versionUrl = routedBaseUrl(config.url || '');
  if (versionUrl) {
    config.baseURL = versionUrl;
  }

  return config;
});

// Response interceptor: handle 401 with silent refresh + retry, detect API down.
//
// When multiple requests 401 simultaneously (e.g., alert polling fires 5 parallel
// calls with an expired token), they all share ONE renewal (renewSession is
// single-flight, and also shared with the proactive renewal). If it succeeds,
// every request retries with the new token. If it fails, the session is marked
// dead, every request rejects and the session-expired dialog is shown once.

/** The API's answer while a web migration run is updating the connection's database. */
export const DATABASE_UPDATING = 'database-updating';
/** How many times one request is sent again after waiting for an update. */
const MAX_DATABASE_UPDATE_RETRIES = 5;

/** True for the API's 503 { reason: "database-updating" } (not a 503 from a server that is down). */
export function isDatabaseUpdatingResponse(status: number | undefined, data: unknown): boolean {
  return status === 503
    && typeof data === 'object' && data !== null
    && (data as { reason?: unknown }).reason === DATABASE_UPDATING;
}

/**
 * True when the API itself answered with a JSON { reason } body, e.g. 503 { reason:
 * "usda-not-configured" } or 502 { reason: "usda-failed" } from the USDA lookup. That proves the API
 * is up, so it must not raise the "API may not be running" overlay; the caller handles it.
 */
export function isApiReasonResponse(data: unknown): boolean {
  return typeof data === 'object' && data !== null
    && typeof (data as { reason?: unknown }).reason === 'string';
}

/** Retry-After in ms: the header, else the body's retryAfterSeconds, else 5 s; at most a minute. */
function retryAfterMs(headers: unknown, data: unknown): number {
  const header = (headers as Record<string, unknown> | undefined)?.['retry-after'];
  const fromHeader = typeof header === 'string' || typeof header === 'number' ? Number(header) : NaN;
  const fromBody = Number((data as { retryAfterSeconds?: unknown } | undefined)?.retryAfterSeconds);
  const seconds = Number.isFinite(fromHeader) ? fromHeader : Number.isFinite(fromBody) ? fromBody : 5;
  return Math.min(Math.max(seconds, 0), 60) * 1000;
}

function connectionIdOf(config: { headers?: unknown } | undefined): number {
  const sent = Number(headerOf(config, 'X-Connection-Id'));
  if (Number.isFinite(sent) && sent > 0) return sent;
  return useConnectionStore.getState().selectedConnection?.connectionId ?? 0;
}

apiClient.interceptors.response.use(
  (response) => {
    // Any successful response means the API is reachable
    reportApiSuccess();
    return response;
  },
  async (error) => {
    // Refused by the session gate above: never sent, so nothing to record or recover.
    if (isSessionExpiredError(error)) return Promise.reject(error);

    // Detect API down: network error (no response), 502/503/504,
    // or 500 from Vite proxy when the backend is unreachable.
    // Vite proxy returns 500 with no JSON body when it can't connect to the backend.
    const status = error.response?.status;
    // Feed the Test Kitchen ring buffer before any of the handling below. Testers are explicitly
    // told not to open dev tools, so this is the only record of what the API actually did when
    // they hit a problem — capture it even for errors we go on to handle silently.
    if (error.code !== 'ERR_CANCELED') {
      recordFailedCall({
        method: (error.config?.method ?? 'get').toUpperCase(),
        url: error.config?.url ?? '(unknown)',
        status: error.response?.status,
        message: error.message,
      });
    }

    // The connection's database is being updated (someone else's login, a tablet or the print
    // agent started a web migration run after this page got in). Not an error and not "API down":
    // show the "Updating your database" screen (DatabaseUpdateGate, tablets included), wait for
    // the run to end, then send the request again. The API refused it before running anything,
    // so sending it again is safe, saves included, and the caller never sees an error.
    if (isDatabaseUpdatingResponse(status, error.response?.data) && error.config) {
      const config = error.config;
      const tries = (config._dbUpdateRetries as number | undefined) ?? 0;
      const connectionId = connectionIdOf(config);
      if (connectionId > 0 && tries < MAX_DATABASE_UPDATE_RETRIES) {
        const receivedAt = Date.now();
        const wait = retryAfterMs(error.response?.headers, error.response?.data);
        useDbUpdateStore.getState().markUpdating(connectionId);
        if (await waitForDatabaseUpdate(connectionId)) {
          // Never sooner than Retry-After (another API instance may still be finishing).
          const left = wait - (Date.now() - receivedAt);
          if (left > 0) await new Promise((resolve) => setTimeout(resolve, left));
          config._dbUpdateRetries = tries + 1;
          return apiClient(config);
        }
      }
      return Promise.reject(error);
    }

    const isNetworkError = !error.response && error.code !== 'ERR_CANCELED';
    const isServerDown = (status === 502 || status === 503 || status === 504)
      && !isApiReasonResponse(error.response?.data);
    // Vite proxy returns 500 with an HTML page containing "ECONNREFUSED"
    // or "ECONNRESET" when the backend is unreachable.  Real API 500s
    // may return empty bodies or error strings — don't treat those as
    // "API down".  We distinguish by checking for proxy error markers
    // in the response body; a real API 500 (even with empty body) comes
    // from Kestrel and won't contain these proxy-specific strings.
    const isProxyError =
      status === 500 &&
      typeof error.response?.data === 'string' &&
      (error.response.data.includes('ECONNREFUSED') ||
        error.response.data.includes('ECONNRESET'));

    if (isNetworkError || isServerDown || isProxyError) {
      reportApiError(
        isNetworkError
          ? 'Unable to reach the server'
          : `Server returned ${status} - API may not be running`,
      );
    }

    // Version-routing mismatch: a request routed to a pinned version slot
    // (baseURL was overridden to a VERSION_API_URLS host in the request
    // interceptor) came back 404 — that slot's build does not contain the
    // route. The React app and its API slot are meant to deploy in lockstep,
    // so this signals a deployment mismatch, not a normal feature gap. Fire a
    // blocking alarm so it can't be mistaken for a transient error.
    // `_skipVersionMismatch`: a call that handles its own 404 (the login check against a slot
    // whose build predates it lets the user in rather than raising the alarm).
    if (status === 404 && !error.config?._skipVersionMismatch) {
      const routedHost = error.config?.baseURL as string | undefined;
      const version = versionForApiHost(routedHost);
      if (version && routedHost) {
        reportVersionMismatch({
          webVersion: version,
          host: routedHost,
          route: (error.config?.url as string | undefined) || '',
        });
      }
    }

    // On a registered tablet a 401 means the server's PIN session ended or the
    // tablet was revoked: back to the PIN pad (or the code screen), never the
    // Keycloak refresh and session-expired dialog below.
    if (status === 401 && useTabletStore.getState().active) {
      useTabletStore.getState().onUnauthorized(error.response?.data?.reason);
      return Promise.reject(error);
    }

    // Handle 401: renew the sign-in once (shared, single-flight) and retry.
    // Skip for requests already retried or flagged as non-session endpoints.
    if (status === 401 && !error.config._retry && !error.config._skipSessionExpiry) {
      return renewAndResend(error, (config) => apiClient(config));
    }
    return Promise.reject(error);
  }
);

/**
 * A signed-in request came back 401: renew the sign-in once (shared,
 * single-flight) and send it again with the new token. If the renewal fails
 * the session is over: mark it dead and show the Session Expired dialog, which
 * also closes the session gate.
 */
export async function renewAndResend<T>(
  error: { config: Record<string, unknown> },
  resend: (config: never) => Promise<T>,
): Promise<T> {
  // Session already known dead: reject immediately, don't retry or refresh.
  // (A proactive renewal may have found it dead while this page still had
  // a working access token; now that a request fails, show the dialog.)
  if (isSessionDead()) {
    reportSessionExpired();
    return Promise.reject(error);
  }

  error.config._retry = true;

  const renewed = await renewSession('request');
  if (renewed) {
    // Retry the original request with the fresh token
    return resend(error.config as never);
  }
  // A request that needed the renewal and did not get it: the session is
  // over (also when it joined a proactive renewal that failed).
  if (!isSessionDead()) markSessionDead();
  reportSessionExpired();
  return Promise.reject(error);
}

/** Called when user logs in again (SessionExpiredDialog → redirect) to reset state */
export function resetAuthInterceptorState() {
  resetSessionRenewal();
}

export default apiClient;
